import { CountryCode } from 'types/countries';
import { ProvinceOrStateCode } from 'utils/province_or_state';
import { isServerSide } from 'utils/host-config';
import { getInsightsHost } from 'utils/host-config';
import { CityStats, TopCities, AllProvinces, InsightsListingType } from './types';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types';
import { InsightsResponse, ProvincesResponse, TopCitiesResponse } from '@zoocasa/go-search';

/**
 * Fetches the top cities (or neighbourhoods) for a given location hierarchy.
 *
 * The results are the most popular areas based on the number of active listings (sale and rent).
 *
 * - `/insights/popular/CA?limit=6`             → top cities across Canada
 * - `/insights/popular/CA/ON?limit=5`           → top cities in Ontario
 * - `/insights/popular/CA/ON/Toronto?limit=5`   → top neighbourhoods in Toronto
 *
 * @param country The country code.
 * @param provinceOrState The province or state code.
 * @param prefixChar Filters result by a prefix character.
 * @param limit The number of results to fetch.
 * @param rentalOnly Whether to only return areas with rental listings. Defaults to false.
 * @param signal An optional AbortSignal to cancel the request.
 * @param host The host to use for the request. Defaults to the result of `getInsightsHost(isServerSide())`
 * @param city When provided, fetches top neighbourhoods within this city (e.g. "Toronto").
 * @returns A promise that resolves to the top cities/neighbourhoods. Returns undefined otherwise.
 */
export async function getTopCities(
  country: CountryCode,
  provinceOrState?: ProvinceOrStateCode,
  prefixChar?: string,
  limit: number = 10,
  rentalOnly: boolean = false,
  signal?: AbortSignal,
  host: string = getInsightsHost(isServerSide()),
  city?: string
): Promise<TopCities[]> {
  let url = `${host}/insights/popular`;
  if (country) {
    url += `/${country}`;
    if (provinceOrState) {
      url += `/${provinceOrState}`;
      if (city) {
        url += `/${encodeURIComponent(city)}`;
      }
    }
  }
  const validPrefix = prefixChar && prefixChar?.length === 1;
  const prefixQuery = validPrefix ? `prefix=${prefixChar}&` : '';
  const rentalQuery = rentalOnly ? 'rentalOnly=true&' : '';
  url += `?${prefixQuery}${rentalQuery}limit=${limit}`;

  const options: FetchOptions = { method: HttpRequestMethodTypes.GET, signal };
  try {
    const response = await fetchWithRetry(url, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = TopCitiesResponse.decode(new Uint8Array(buffer));
      return apiResp.topCities;
    } else {
      console.error(`endpoint=insights/popular,url=${url},status=${response?.status},status_text=${response?.statusText}`);
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch TopCities request was aborted');
    } else {
      console.error(`endpoint=insights/popular,url=${url},error=${error}`);
    }
    return undefined;
  }
}

/**
 * Fetches all provinces or states for a given country.
 *
 * @param country The country code.
 * @param signal An optional AbortSignal to cancel the request.
 * @param host The host to use for the request. Defaults to the result of `getInsightsHost(isServerSide())`
 * @returns A promise that resolves to the top cities. Returns undefined otherwise.
 */
export async function getAllProvincesOrStates(country: CountryCode, signal?: AbortSignal, host: string = getInsightsHost(isServerSide())): Promise<AllProvinces[]> {
  const url = `${host}/insights/provinces/${country}`;
  const options: FetchOptions = { method: HttpRequestMethodTypes.GET, signal };
  try {
    const response = await fetchWithRetry(url, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = ProvincesResponse.decode(new Uint8Array(buffer));
      return apiResp.provinces;
    } else {
      console.error(`endpoint=insights/provinces,url=${url},status=${response?.status},status_text=${response?.statusText}`);
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch all provinces request was aborted');
    } else {
      console.error(`endpoint=insights/provinces,url=${url},error=${error}`);
    }
    return undefined;
  }
}

/** The insights service's `type` query value for each market; for sale is its default. */
const INSIGHTS_TYPE_PARAM: Record<InsightsListingType, string | null> = {
  sale: null,
  rent: 'rent',
  sold: 'sold',
};

/**
 * Fetches the city stats for a given city in a given province or state in a given country.
 *
 * @param country The country code.
 * @param province The province or state code. Required to prevent expensive country-wide queries.
 * @param city The city to fetch stats for.
 * @param signal An optional AbortSignal to cancel the request.
 * @param host The host to use for the request. Defaults to the result of `getInsightsHost(isServerSide())`
 * @param listingType The market the stats describe. Defaults to homes for sale.
 * @returns A promise that resolves to the city stats. Returns undefined if province is not provided or on error.
 */
export async function getCityStats(
  country: CountryCode,
  province: ProvinceOrStateCode,
  city?: string,
  signal?: AbortSignal,
  host: string = getInsightsHost(isServerSide()),
  listingType: InsightsListingType = 'sale'
): Promise<CityStats | undefined> {
  // Validate required parameters - province is required to prevent expensive country-wide queries
  // that can overwhelm Elasticsearch (e.g., /insights/stats/CA without province)
  if (!country || !province) {
    console.warn(`getCityStats called without required parameters: country=${country}, province=${province}. Skipping request to prevent expensive query.`);
    return undefined;
  }

  const typeParam = INSIGHTS_TYPE_PARAM[listingType];
  const url = `${host}/insights/stats/${country}/${province.toUpperCase()}${city ? `/${city}` : ''}${typeParam ? `?type=${typeParam}` : ''}`;
  const options: FetchOptions = { method: HttpRequestMethodTypes.GET, signal };

  try {
    const response = await fetchWithRetry(url, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = InsightsResponse.decode(new Uint8Array(buffer));
      return apiResp;
    } else {
      console.error(`endpoint=insights/stats,url=${url},status=${response?.status},status_text=${response?.statusText}`);
      return undefined;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch CityStats request was aborted');
    } else {
      console.error(`endpoint=insights/stats,url=${url},error=${error}`);
    }
    return undefined;
  }
}
